import { useEffect, useRef, useState, FormEvent } from 'react';

export default function ContactPage() {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.scroll-animate').forEach((el) => observerRef.current?.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    // Update page title and meta tags
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = 'お問い合わせ | 合同会社Lill - AI導入支援・システム開発のご相談';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'サービスに関するご質問やお見積もり等、お気軽にお問い合わせください。通常1〜2営業日以内にご返信いたします。Open Claw導入、AI活用、システム開発など幅広く対応します。');
    }

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', siteUrl + '/contact');
    }

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'お問い合わせ | 合同会社Lill');
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'サービスに関するご質問やお見積もり等、お気軽にお問い合わせください。通常1〜2営業日以内にご返信いたします。');
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('href', siteUrl + '/contact');
    }

    // Schema.org JSON-LD for Contact page
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'お問い合わせ | 合同会社Lill',
      description: 'サービスに関するご質問やお見積もり等、お気軽にお問い合わせください。通常1〜2営業日以内にご返信いたします。',
      url: `${siteUrl}/contact`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: siteUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'お問い合わせ',
            item: `${siteUrl}/contact`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }
    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d6onjpblgirdouph2uj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          company: formData.company,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: formData.category,
          message: formData.message,
        }).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', name: '', email: '', phone: '', category: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── Hero ── */}
      <section className="relative py-20 sm:py-32 lg:py-40 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=800&fit=crop"
            alt="お問い合わせ"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1a2744]/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-4 sm:mb-6 tracking-tight">CONTACT</h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-white/90 tracking-wide">お問い合わせ</p>
        </div>
      </section>

      {/* ── Form Section ── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="scroll-animate text-center mb-12 sm:mb-16">
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              サービスに関するご質問やお見積もり等、お気軽にお問い合わせください。<br className="hidden sm:block" />
              通常1〜2営業日以内にご返信いたします。
            </p>
          </div>

          <div className="scroll-animate">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-[#1a2744] mb-2">
                  会社名（任意）
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#1a2744] mb-2">
                  お名前<span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#1a2744] mb-2">
                  メールアドレス<span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#1a2744] mb-2">
                  電話番号（任意）
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                  placeholder="03-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-[#1a2744] mb-2">
                  お問い合わせ種別
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                >
                  <option value="">選択してください</option>
                  <option value="Open Claw導入">Open Claw導入</option>
                  <option value="AI活用・コンサル">AI活用・コンサル</option>
                  <option value="サクッとHP">サクッとHP</option>
                  <option value="システム開発">システム開発</option>
                  <option value="web3支援">web3支援</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#1a2744] mb-2">
                  お問い合わせ内容<span className="text-red-600 ml-1">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent resize-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                />
                <div className="text-xs sm:text-sm text-gray-500 mt-2 text-right">
                  {formData.message.length} / 500文字
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 sm:py-5 bg-[#1a2744] text-white font-medium rounded-lg text-base hover:bg-[#1a2744]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 sm:py-4 rounded-lg text-sm sm:text-base">
                  お問い合わせを受け付けました。ご連絡ありがとうございます。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 sm:py-4 rounded-lg text-sm sm:text-base">
                  送信に失敗しました。お手数ですが、もう一度お試しください。
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}